import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { Client, Conversation, Message } from '@twilio/conversations'
import { differenceInSeconds } from 'date-fns'
import { get } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'

import {
  applyMessageAddedToConversation,
  applyMessageAddedToProviderPairConversation,
  getConversationAddedTags,
} from './twilioMessagingUtils'
import { getAuthConfig, getAuthUserId } from '../../../data/auth/authSelectors'
import { baseApi, RTKQueryTag } from '../../../data/baseApi'
import { updateEndpointCache } from '../../../data/rtkQuery'
import { useAppDispatch } from '../../../data/store'
import { useConversationsData } from '../../../lyraTherapy/messages/data/hooks/useConversationsData'
import messagesApi, {
  refreshProviderPairConversations,
  useGetAccessTokenQuery,
} from '../../../lyraTherapy/messages/data/messagesApi'
import { getConversationsClient } from '../../../lyraTherapy/messages/data/messagesSelectors'
import { setConversationsClient } from '../../../lyraTherapy/messages/data/messagesSlice'
import { TwilioConversations } from '../../twilio-conversations'

const TIME_DIFFERENCE_TO_FETCH_CONVERSATIONS_SECONDS = 10

/**
 * Hook to use Twilio messaging.
 * Creates a Twilio conversations client
 */
export default () => {
  const config = useSelector(getAuthConfig)
  const { data: accessToken } = useGetAccessTokenQuery(undefined, {
    skip: !config?.bcPlatform || config?.hideMessagesNav,
  })
  const dispatch = useAppDispatch()
  const userId = useSelector(getAuthUserId)
  const conversationsClient = useSelector(getConversationsClient)
  const { data: conversations } = useConversationsData(undefined, {
    skip: !config?.bcPlatform || config?.hideMessagesNav,
  })
  const { refetchOnConversationAdded } = useFlags()

  useEffect(() => {
    if (accessToken && !conversationsClient && config?.bcPlatform) {
      const twilioClient = new TwilioConversations(accessToken)
      dispatch(setConversationsClient(twilioClient as Client))
    }
  }, [accessToken, config?.bcPlatform, conversationsClient, dispatch])

  useEffect(() => {
    if (!conversationsClient) {
      return
    }

    const onConversationAdded = async (conversation: Conversation) => {
      if (
        !conversation.dateCreated ||
        differenceInSeconds(new Date(), conversation.dateCreated) > TIME_DIFFERENCE_TO_FETCH_CONVERSATIONS_SECONDS
      ) {
        return
      }
      if (conversations?.some((c) => c.conversation_id === conversation.sid)) {
        return
      }
      const tags = getConversationAddedTags(conversation.attributes)
      // Pair discovery must not depend on the legacy patient-list flag or query readiness.
      if (tags.includes(RTKQueryTag.PROVIDER_PAIR_CONVERSATION)) {
        await dispatch(refreshProviderPairConversations())
      }
      if (!refetchOnConversationAdded || !conversations) {
        return
      }
      dispatch(
        baseApi.util.invalidateTags([
          RTKQueryTag.APPOINTMENT,
          ...tags.filter((tag) => tag !== RTKQueryTag.PROVIDER_PAIR_CONVERSATION),
          RTKQueryTag.CLIENT,
        ]),
      )
    }
    conversationsClient.on('conversationAdded', onConversationAdded)
    return () => {
      conversationsClient.off('conversationAdded', onConversationAdded)
    }
  }, [conversations, conversationsClient, dispatch, refetchOnConversationAdded, userId])

  useEffect(() => {
    const incrementCount = (message: Message) => {
      const messageSentDatetime = new Date().toISOString()
      const messageAuthorId = get(message, 'state.attributes.from')
      const updateConversation = (conversation: ConversationObject) => {
        applyMessageAddedToConversation(conversation, messageAuthorId, userId, messageSentDatetime)
      }
      dispatch(
        updateEndpointCache(messagesApi, 'getConversations')
          .where({ providerId: userId })
          .items({ conversation_id: message.conversation.sid })
          .build(updateConversation),
      )
      dispatch(
        updateEndpointCache(messagesApi, 'getPatientConversations')
          .where({ providerId: userId })
          .items({ conversation_id: message.conversation.sid })
          .build(updateConversation),
      )
      dispatch(
        updateEndpointCache(messagesApi, 'getProviderPairConversations')
          .where({ providerId: userId })
          .items({ external_conversation_id: message.conversation.sid })
          .build((conversation) =>
            applyMessageAddedToProviderPairConversation(conversation, messageAuthorId, userId, messageSentDatetime),
          ),
      )
    }

    if (conversationsClient) {
      conversationsClient.on('messageAdded', incrementCount)
    }
    return () => {
      if (conversationsClient) {
        conversationsClient.off('messageAdded', incrementCount)
      }
    }
  }, [conversationsClient, dispatch, userId])
}
